import { useEffect, useEffectEvent } from 'react';

/** The single-letter shortcuts of the tickets pages (spec: C create, A assign, P priority, L labels, S status, J/K move). */
export type TicketKey = 'c' | 'a' | 'p' | 'l' | 's' | 'j' | 'k';

const KEYS: ReadonlySet<string> = new Set<TicketKey>(['c', 'a', 'p', 'l', 's', 'j', 'k']);

/** True for a place where letters are text: a field, a text area, a select or an editable element. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
}

/**
 * Linear-style single-letter shortcuts. A key is handled only when the page wants it (handlers has it), never while
 * someone types (an input, a text area, a select or an editable element has the event), never with a modifier (so
 * the browser's own shortcuts and ⌘K stay theirs), never inside or while a menu or list box is open, and never while a
 * dialog is open (enabled false, or a modal dialog in the document: its own fields and buttons own the keyboard then).
 */
export function useTicketKeys(handlers: Partial<Record<TicketKey, () => void>>, enabled = true): void {
  const handle = useEffectEvent((event: KeyboardEvent) => {
    if (event.defaultPrevented || event.repeat) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key.toLowerCase();
    if (!KEYS.has(key)) return;
    if (isTyping(event.target)) return;
    // Inside an open menu or list box the letters belong to it (and a second menu must not open over the first).
    if (event.target instanceof Element && event.target.closest('[role="menu"], [role="listbox"]')) return;
    if (document.querySelector('[role="menu"], [role="listbox"]')) return;
    if (document.querySelector('[role="dialog"][aria-modal="true"], dialog[open]')) return;
    const run = handlers[key as TicketKey];
    if (!run) return;
    event.preventDefault();
    run();
  });
  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent) => handle(event);
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [enabled]);
}
